import type React from "react";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import MyLocationOutlinedIcon from "@mui/icons-material/MyLocationOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import PersonPinCircleOutlinedIcon from "@mui/icons-material/PersonPinCircleOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import {
  ActionButton,
  Badge,
  CardContainer,
  CardWrapper,
  Divider,
  FeatureIcon,
  FeatureItem,
  FeatureList,
  FeatureText,
  Header,
  PriceRow,
  PriceNote,
  PlanSubtitle,
  PlanTitle,
} from "./styles";

export type PlanCardVariant = "bronze" | "prata" | "ouro" | "diamante";

export type PlanCardFeatureType =
  | "painel"
  | "raio"
  | "keywords"
  | "impulsionamentos"
  | "ia"
  | "suporte"
  | "consultor"
  | "landing"
  | "vip";

export type PlanCardFeature = {
  id?: string;
  label: string;
  description?: string;
  type?: PlanCardFeatureType;
  icon?: React.ReactNode;
  highlighted?: boolean;
  disabled?: boolean;
};

export type PlanCardProps = {
  title: string;
  subtitle?: string;
  price: string | number;
  period?: string;
  priceNote?: string;
  badgeText?: string;
  ctaLabel: string;
  features: PlanCardFeature[];
  variant?: PlanCardVariant;
  accentColor?: string;
  isSelected?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
};

const accentByVariant: Record<PlanCardVariant, string> = {
  bronze: "#8D6E4B",
  prata: "#3B7CBF",
  ouro: "#F4B400",
  diamante: "#34A853",
};

const iconByType: Record<PlanCardFeatureType, React.ReactNode> = {
  painel: <DashboardOutlinedIcon fontSize="small" />,
  raio: <MyLocationOutlinedIcon fontSize="small" />,
  keywords: <KeyOutlinedIcon fontSize="small" />,
  impulsionamentos: <RocketLaunchOutlinedIcon fontSize="small" />,
  ia: <SmartToyOutlinedIcon fontSize="small" />,
  suporte: <SupportAgentOutlinedIcon fontSize="small" />,
  consultor: <PersonPinCircleOutlinedIcon fontSize="small" />,
  landing: <PublicOutlinedIcon fontSize="small" />,
  vip: <WorkspacePremiumOutlinedIcon fontSize="small" />,
};

const formatPrice = (value: string | number) =>
  typeof value === "number" ? value.toLocaleString("pt-BR") : value;

export const PlanCardNew: React.FC<PlanCardProps> = ({
  title,
  subtitle,
  price,
  period = "/mês",
  priceNote,
  badgeText,
  ctaLabel,
  features,
  variant = "prata",
  accentColor,
  isSelected = false,
  disabled = false,
  onSelect,
}) => {
  const resolvedAccent = accentColor ?? accentByVariant[variant];

  return (
    <CardWrapper>
      {badgeText && <Badge $accentColor={resolvedAccent}>{badgeText}</Badge>}
      <CardContainer
        $accentColor={resolvedAccent}
        $selected={isSelected}
        $clickable={Boolean(onSelect)}
        aria-disabled={disabled}
        role={onSelect ? "button" : undefined}
        tabIndex={onSelect ? 0 : undefined}
        onClick={() => !disabled && onSelect?.()}
        onKeyDown={(e) => {
          if (!onSelect || disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        <Header>
          <PlanTitle>{title}</PlanTitle>
          {subtitle && <PlanSubtitle>{subtitle}</PlanSubtitle>}
          <PriceRow>
            <span className="currency">R$</span>
            <span className="value">{formatPrice(price)}</span>
            <span className="period">{period}</span>
          </PriceRow>
          {priceNote && <PriceNote>{priceNote}</PriceNote>}
        </Header>

        <Divider />

        <FeatureList>
          {features.map((feature, index) => {
            const iconNode =
              feature.icon ??
              iconByType[feature.type ?? "painel"] ??
              iconByType.painel;

            return (
              <FeatureItem
                key={feature.id ?? `${feature.label}-${index}`}
                $highlighted={Boolean(feature.highlighted)}
                $muted={Boolean(feature.disabled)}
              >
                <FeatureIcon
                  $accentColor={resolvedAccent}
                  $muted={Boolean(feature.disabled)}
                >
                  {iconNode}
                </FeatureIcon>
                <FeatureText>
                  <span className="label">{feature.label}</span>
                  {feature.description && (
                    <span className="description">{feature.description}</span>
                  )}
                </FeatureText>
              </FeatureItem>
            );
          })}
        </FeatureList>

        <ActionButton
          $accentColor={resolvedAccent}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            if (!disabled) {
              onSelect?.();
            }
          }}
          disabled={!onSelect || disabled}
        >
          {ctaLabel}
        </ActionButton>
      </CardContainer>
    </CardWrapper>
  );
};
